/**
 * Frescura del forecast por producto (spec bajas-odoo18, CR-8/CR-9).
 * Misma regla de 24 h y el mismo Intl.RelativeTimeFormat que usaba el pie del
 * diálogo cuando marcaba todo el pedido con el timestamp más viejo.
 */

export const FORECAST_STALE_MS = 24 * 60 * 60 * 1000;

export interface ForecastFreshness {
  ageMs: number;
  isStale: boolean;
  /** Ej. "hace 133 días", "hace 3 horas", "hace 12 minutos". */
  label: string;
}

const rtf = new Intl.RelativeTimeFormat("es", { numeric: "auto" });

export function relativeAgeLabel(ageMs: number): string {
  const hours = Math.round(ageMs / (60 * 60 * 1000));
  if (hours < 1) return rtf.format(-Math.round(ageMs / 60000), "minute");
  if (hours < 48) return rtf.format(-hours, "hour");
  return rtf.format(-Math.round(hours / 24), "day");
}

export function freshnessOf(
  iso: string | null | undefined,
  now: number = Date.now(),
): ForecastFreshness | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const ageMs = Math.max(0, now - t);
  return { ageMs, isStale: ageMs > FORECAST_STALE_MS, label: relativeAgeLabel(ageMs) };
}

export interface SelectionFreshness {
  /** Cuántos productos de la selección tienen forecast > 24 h. */
  staleCount: number;
  /** Frescura del producto más viejo (base de la etiqueta). */
  oldest: ForecastFreshness;
  /** Texto del pie del diálogo. */
  footer: string;
}

export function selectionFreshness(
  timestamps: Array<string | null | undefined>,
  now: number = Date.now(),
): SelectionFreshness | null {
  const items = timestamps
    .map((t) => freshnessOf(t, now))
    .filter((f): f is ForecastFreshness => f !== null);
  if (!items.length) return null;
  const oldest = items.reduce((a, b) => (b.ageMs > a.ageMs ? b : a));
  const staleCount = items.filter((f) => f.isStale).length;
  const footer =
    staleCount > 0
      ? `${staleCount} ${staleCount === 1 ? "producto" : "productos"} con forecast desactualizado (el más viejo ${oldest.label})`
      : `Forecast actualizado ${oldest.label}`;
  return { staleCount, oldest, footer };
}
